import { Id, toast, UpdateOptions } from "react-toastify";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchItem, postItem } from "../../queries/queries-helper";
import ResponseApi from "../../models/interfaces/response-api";
import { sleep } from "../../utils/async-utils";
import { JobAdapter, UnifiedJobStatus } from "../../models/interfaces/dtos/job.ts";

export interface JobStatusMessage {
    pending?: string;
    success?: string;
    error?: string;
}

const DEFAULT_MESSAGES: Required<JobStatusMessage> = {
    pending: "Processus en cours...",
    success: "Travail terminé.",
    error: "Une erreur est survenue."
};

const POLLING_INTERVAL = 2000;
const MAX_RETRIES = 5;

interface UseJobAsyncOptions<TJob> {
    jobUrl: () => string;
    /** Request body, or a synchronous factory evaluated immediately before sending. */
    jobData?: unknown | (() => unknown);
    getExistingJobUrl?: string;
    getStatusUrl: (jobId: string) => string;
    adapter: JobAdapter<TJob>;
    onJobDone?: (jobId: string, toastId: Id) => Promise<void> | void;
    jobFinished?: (succeeded: boolean, isExistingJob: boolean) => Promise<void> | void;
    onError?: () => void;
    messages?: JobStatusMessage;
}

const useJobAsync = <TJob,>({
    jobUrl,
    jobData,
    getExistingJobUrl,
    getStatusUrl,
    adapter,
    onJobDone,
    jobFinished,
    onError,
    messages
}: UseJobAsyncOptions<TJob>) => {

    const toastIdRef = useRef<Id | null>(null);
    const isMounted = useRef(true);

    const jobUrlRef = useRef(jobUrl);
    const onJobDoneRef = useRef(onJobDone);
    const jobFinishedRef = useRef(jobFinished);
    const onErrorRef = useRef(onError);
    const messagesRef = useRef(messages);

    useEffect(() => {
        jobUrlRef.current = jobUrl;
    }, [jobUrl]);

    useEffect(() => {
        onJobDoneRef.current = onJobDone;
    }, [onJobDone]);

    useEffect(() => {
        jobFinishedRef.current = jobFinished;
    }, [jobFinished]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => () => {
        isMounted.current = false;
    }, []);

    const updateToast = useCallback((options: UpdateOptions) => {
        if (toastIdRef.current && isMounted.current) {
            toast.update(toastIdRef.current, {
                isLoading: false,
                autoClose: 5000,
                closeButton: true,
                ...options
            });
        }
    }, []);

    const handleError = useCallback((message?: string) => {
        updateToast({
            render: message ?? messagesRef.current?.error ?? DEFAULT_MESSAGES.error,
            type: "error",
            autoClose: false
        });

        onErrorRef.current?.();
    }, [updateToast]);

    const checkPreviouslyLaunched = useCallback(async (): Promise<string | null> => {

        if (!getExistingJobUrl) {
            return null;
        }

        try {

            const existingJob = await fetchItem<TJob>(getExistingJobUrl);

            if (!existingJob) {
                return null;
            }

            const status = adapter.getStatus(existingJob);

            if (
                status === UnifiedJobStatus.Pending ||
                status === UnifiedJobStatus.Processing
            ) {
                return adapter.getId(existingJob);
            }

            return null;

        } catch {
            return null;
        }

    }, [adapter, getExistingJobUrl]);

    const startPolling = useCallback(async (jobId: string) => {

        let retries = 0;
        let succeeded = false;

        const pollStatus = async (): Promise<boolean> => {

            try {

                const statusDto = await fetchItem<TJob>(getStatusUrl(jobId));

                if (!statusDto) {
                    handleError("Job introuvable.");
                    return true;
                }

                const status = adapter.getStatus(statusDto);

                if (status === UnifiedJobStatus.Success) {

                    try {
                        await onJobDoneRef.current?.(
                            jobId,
                            toastIdRef.current!
                        );
                    } catch {
                        handleError();
                        return true;
                    }

                    updateToast({
                        render: messagesRef.current?.success ?? DEFAULT_MESSAGES.success,
                        type: "success"
                    });

                    succeeded = true;
                    return true;
                }

                if (status === UnifiedJobStatus.Error) {
                    handleError();
                    return true;
                }

                await sleep(POLLING_INTERVAL);
                return false;

            } catch {

                if (retries < MAX_RETRIES) {
                    retries += 1;
                    await sleep(POLLING_INTERVAL);
                    return false;
                }

                handleError();
                return true;
            }
        };

        let finished = false;

        while (!finished && isMounted.current) {
            // eslint-disable-next-line no-await-in-loop
            finished = await pollStatus();
        }

        return succeeded;
    }, [
        adapter,
        getStatusUrl,
        handleError,
        updateToast
    ]);

    const [isLoading, setIsLoading] = useState(false);

    const launchJob = useCallback(async () => {

        setIsLoading(true);

        try {

            toastIdRef.current = toast.loading(
                messagesRef.current?.pending ?? DEFAULT_MESSAGES.pending
            );

            const existingJobId = await checkPreviouslyLaunched();

            if (existingJobId) {
                const succeeded = await startPolling(existingJobId);
                if (isMounted.current) await jobFinishedRef.current?.(succeeded, true);
                return;
            }

            const data = typeof jobData === "function" ? jobData() : jobData;
            const response = await postItem<ResponseApi<TJob>>(jobUrlRef.current(), data);

            if (!response?.success) {
                handleError(response?.errorMessages?.[0]);
                return;
            }

            const jobId = adapter.getId(response.entity);

            const succeeded = await startPolling(jobId);
            if (isMounted.current) await jobFinishedRef.current?.(succeeded, false);

        } catch (error) {
            handleError(error instanceof Error ? error.message : undefined);
        } finally {

            if (isMounted.current) {
                setIsLoading(false);
            }
        }

    }, [
        adapter,
        checkPreviouslyLaunched,
        handleError,
        jobData,
        startPolling
    ]);

    return {
        launchJob,
        isLoading
    };
};

export default useJobAsync;
